/* Shopify remains the price authority. Only our marked, zero-priced gifts are retained. */
(function () {
  'use strict';
  const property = '_infraredi_gift';
  const marker = 'pro-series-body-wrap';
  const quantity = item => Math.max(0, Number(item.quantity) || 0);
  const isGift = (item, config) => String(item.product_id) === String(config.productId)
    && item.properties?.[property] === marker;
  const eligible = (item, config) => !item.selling_plan_allocation && !item.selling_plan
    && config.eligibleProductIds.some(id => String(id) === String(item.product_id));
  const entitlement = (cart, config) => config.enabled
    ? (cart.items || []).filter(item => eligible(item, config)).reduce((n, item) => n + quantity(item), 0) : 0;

  function plan(cart, config, allowAdd = false) {
    const items = cart.items || [];
    const manual = items.filter(item => String(item.product_id) === String(config.productId)
      && !isGift(item, config) && !item.selling_plan_allocation).reduce((n, item) => n + quantity(item), 0);
    let remaining = Math.max(0, entitlement(cart, config) - manual);
    const updates = {};
    for (const item of items.filter(item => isGift(item, config))) {
      const keep = Math.min(remaining, quantity(item));
      if (keep !== quantity(item)) updates[item.key] = keep;
      remaining -= keep;
    }
    return { updates, add: allowAdd && config.enabled && config.available ? remaining : 0 };
  }

  function createReconciler(config, transport) {
    return async function reconcile(cart, allowAdd) {
      let changed = false;
      let rejected = false;
      const wanted = plan(cart, config, allowAdd);
      if (Object.keys(wanted.updates).length) {
        cart = await transport.update(wanted.updates);
        changed = true;
      }
      if (wanted.add) {
        // Never retry an uncertain add: inventory errors can partially change a Shopify cart.
        try { await transport.add({ id: config.variantId, quantity: wanted.add, properties: { [property]: marker } }); }
        catch (_error) { rejected = true; }
        cart = await transport.read();
        changed = true;
      }
      // Check the final Shopify allocation, including non-combining discount codes.
      const paid = (cart.items || []).filter(item => isGift(item, config) && item.final_line_price !== 0);
      if (paid.length) {
        cart = await transport.update(Object.fromEntries(paid.map(item => [item.key, 0])));
        rejected = true;
        changed = true;
      }
      if ((cart.items || []).some(item => isGift(item, config) && item.final_line_price !== 0)) {
        throw new Error('Free gift could not be verified');
      }
      return { cart, changed, rejected };
    };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { plan, entitlement, eligible, isGift, createReconciler };
  }
  if (typeof window === 'undefined') return;
  const node = document.getElementById('ir-pro-gift-config');
  if (!node || window.InfrarediProGift) return;
  const config = JSON.parse(node.textContent);
  const nativeFetch = window.fetch.bind(window);
  const route = window.Shopify?.routes?.root || '/';
  let queue = Promise.resolve();
  let pending = 0;
  let unsafe = false;
  let notice;
  function setUnsafe(value) {
    unsafe = value;
    document.documentElement.toggleAttribute('data-ir-gift-unsafe', value);
  }

  function message(text) {
    if (!document.body) return;
    if (!notice || !notice.isConnected) {
      notice = document.createElement('div');
      notice.className = 'ir-gift-notice';
      notice.setAttribute('role', 'alert');
      notice.addEventListener('click', () => notice.remove());
      document.body.appendChild(notice);
    }
    notice.textContent = text;
  }
  async function json(url, options) {
    const response = await nativeFetch(url, options);
    if (!response.ok) throw new Error('Unable to verify the free wrap');
    return response.json();
  }
  const transport = {
    read: () => json(`${route}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } }),
    update: updates => json(`${route}cart/update.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates })
    }),
    add: item => json(`${route}cart/add.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [item] })
    })
  };
  const reconcile = createReconciler(config, transport);
  function enqueue(action) {
    pending++;
    document.documentElement.setAttribute('data-ir-gift-busy', '');
    const next = queue.then(action);
    queue = next.catch(() => {}).finally(() => {
      pending--;
      if (!pending) document.documentElement.removeAttribute('data-ir-gift-busy');
    });
    return next;
  }

  async function runMutation(input, options, action) {
    let before;
    if (action === 'change' || action === 'update') {
      try { before = await transport.read(); } catch (_error) { /* Removal still needs to work. */ }
    }
    const response = await nativeFetch(input, options);
    let original;
    try { original = await response.clone().json(); } catch (_error) { return response; }
    try {
      let cart = await transport.read();
      const addedItems = original.items || [original];
      const allowAdd = response.ok && (action === 'add'
        ? addedItems.some(item => eligible(item, config))
        : before && entitlement(cart, config) > entitlement(before, config));
      const result = await reconcile(cart, allowAdd);
      setUnsafe(false);
      if (result.rejected) message('The free Body Wrap could not be applied with the current cart or availability. We have removed any automatically added paid wrap.');
      if (!result.changed) return response;
      let sections = original.sections;
      if (sections && Object.keys(sections).length) {
        const sectionURL = new URL(window.location.href);
        sectionURL.searchParams.set('sections', Object.keys(sections).join(','));
        sections = await json(sectionURL.href, { cache: 'no-store' });
      }
      // Preserve the caller's add/error response contract and replace stale rendered sections.
      const body = action === 'add' || !response.ok ? { ...original, sections } : { ...result.cart, sections };
      const headers = new Headers(response.headers);
      headers.delete('content-length');
      headers.delete('content-encoding');
      return new Response(JSON.stringify(body), { status: response.status, statusText: response.statusText, headers });
    } catch (_error) {
      setUnsafe(true);
      message('Your cart changed, but we could not verify the free wrap. Please refresh your cart before checking out.');
      // An already successful panel add must never look like a failed add (and invite duplicates).
      return response;
    }
  }

  // Scope interception to this storefront's cart writes. Serialize the native cart,
  // drawer, quick-add and shipping-protection requests so sections reflect the final gift state.
  window.fetch = function (input, options) {
    let url;
    try { url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, window.location.href); }
    catch (_error) { return nativeFetch(input, options); }
    const method = String(options?.method || input?.method || 'GET').toUpperCase();
    const action = url.origin === window.location.origin && method === 'POST'
      && url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?$/)?.[1];
    if (!action) return nativeFetch(input, options);
    return enqueue(() => runMutation(input, options, action));
  };
  window.InfrarediProGift = { ready: () => queue };

  function guardCheckout(event) {
    const target = event.target.closest?.('[name="checkout"], a[href*="/checkout"], [data-shopify="payment-button"]');
    const checkoutForm = event.type === 'submit' && (event.submitter?.name === 'checkout' || /\/checkout/.test(event.target.action || ''));
    if (!(target || checkoutForm) || !(pending || unsafe)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    message(unsafe ? 'Please refresh your cart so we can verify your free wrap before checkout.' : 'We are updating your free wrap. Please try checkout again in a moment.');
  }
  document.addEventListener('click', guardCheckout, true);
  document.addEventListener('submit', guardCheckout, true);
  document.addEventListener('DOMContentLoaded', () => {
    // US Pro pages currently use Notify Me. Keep their existing purchase availability.
    const root = document.querySelector('[data-ir-purchase-mode="us-notify"]');
    const anchor = root && [...root.querySelectorAll('button, [role="button"]')].find(el => /notify me/i.test(el.textContent));
    const template = document.getElementById('ir-pro-gift-template');
    if (anchor && template && !root.querySelector('[data-ir-pro-gift]')) anchor.after(template.content.cloneNode(true));
    // Clean up marked gifts after refresh/another tab, without adding unsolicited gifts.
    enqueue(async () => {
      try {
        const result = await reconcile(await transport.read(), false);
        setUnsafe(false);
        if (result.changed) document.dispatchEvent(new CustomEvent('cart:reload', { bubbles: true }));
      } catch (_error) { setUnsafe(true); }
    });
  }, { once: true });
})();
